import {
  inject,
  provide,
  reactive,
  ref,
  computed,
  type InjectionKey,
  type ComputedRef,
  type Ref
} from 'vue'

/**
 * Per-field AI provenance: the raw inference info string the AI returned
 * when populating this field, plus a flag indicating the value was AI-set.
 */
export interface AiFieldHint {
  inferenceInfo: string | null
  setAt: number
}

/**
 * Shape of a single AI-extracted value. Matches `AiExtractionValue<T>` in
 * `@gat-ship/types` — kept as a local structural type so this composable
 * has zero runtime dependency on the types package.
 */
export interface AiExtractionValue<T> {
  value: T | null
  inferenceInfo: string | null
}

/**
 * The reactive AI hint context that `useAiFormFill` provides and
 * `useAiFieldHint` / `useAiLoading` inject. Keyed by tanstack field name
 * (string path) for the per-field hints map.
 */
export interface AiFormFillContext {
  hints: Record<string, AiFieldHint | undefined>
  /** True while an `applyAsync` operation is in flight. */
  isApplying: Readonly<Ref<boolean>>
  /** Drop the hint when the user manually edits a field. */
  clearHint: (name: string) => void
}

const AI_FORM_FILL_KEY: InjectionKey<AiFormFillContext> = Symbol(
  'ai-form-fill-context'
)

/**
 * Minimal shape of a tanstack form instance we depend on. Typed loosely
 * so this file has zero compile coupling on `@tanstack/vue-form` generics.
 *
 * The `setFieldValue` parameter types use `any` because tanstack's strict
 * field-name generic narrows to a union of literal keys, which is
 * contravariant against `(name: string, value: unknown)`. Relaxing here
 * lets any concrete tanstack form satisfy the constraint at the call site
 * without forcing every consumer to thread the full generic chain.
 */

export interface MinimalAiFormApi {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFieldValue: (name: any, value: any) => void
}

export interface UseAiFormFillOptions<TResponse extends object> {
  /**
   * Optional mapping from form field name → AI response key. Defaults to
   * identity (form field "name" reads `response.name`). Use this when the
   * AI schema uses different naming than your form.
   */
  mapping?: Partial<Record<string, keyof TResponse & string>>
}

/**
 * Wires AI-extract responses into a tanstack form, decoupled from the
 * form primitive itself. Call this once per form that should accept AI
 * fill, then call `applyResponse(aiResponse)` whenever you receive one.
 *
 * Any descendant `FormTextField` / `FormTextareaField` will automatically
 * show an AI provenance indicator for fields that were AI-populated, via
 * a provided context — no explicit per-field wiring required.
 *
 * The form layer itself stays AI-agnostic; this composable is the only
 * place that knows about AI extract responses.
 */
export function useAiFormFill<TResponse extends object>(
  form: MinimalAiFormApi,
  options: UseAiFormFillOptions<TResponse> = {}
) {
  const hints = reactive<Record<string, AiFieldHint | undefined>>({})
  const isApplying = ref(false)

  function clearHint(name: string) {
    delete hints[name]
  }

  function isExtractionValue(x: unknown): x is AiExtractionValue<unknown> {
    return (
      typeof x === 'object' &&
      x !== null &&
      'value' in x &&
      'inferenceInfo' in x
    )
  }

  /**
   * Push an AI extract response into the form. Each top-level entry of
   * the response that matches a mapped (or identity-named) form field is
   * written to the form via `setFieldValue` and tagged with its inference
   * info so the UI can show provenance.
   */
  function applyResponse(response: TResponse) {
    const mapping = options.mapping ?? {}
    const fieldNames = new Set<string>([
      ...Object.keys(mapping),
      ...Object.keys(response)
    ])
    for (const fieldName of fieldNames) {
      const responseKey = mapping[fieldName] ?? fieldName
      const entry = (response as Record<string, unknown>)[responseKey]
      if (!isExtractionValue(entry)) continue
      if (entry.value === null || entry.value === undefined) continue
      form.setFieldValue(fieldName, entry.value)
      hints[fieldName] = {
        inferenceInfo: entry.inferenceInfo ?? null,
        setAt: Date.now()
      }
    }
  }

  /**
   * Run an async operation that produces an AI extract response, with the
   * `isApplying` flag toggled around it. Any descendant field component
   * inside this AI context will display a loading state for the duration
   * — disabled inputs and a IconLoading where the sparkle indicator goes —
   * without the consumer wiring per-field loading props.
   *
   * Accepts either a producer function (recommended, lets you put
   * pre-flight work like fetching inside the wrapped scope) or a raw
   * Promise (for the simple "I already have the call" case).
   */
  async function applyAsync(
    producer: (() => Promise<TResponse>) | Promise<TResponse>
  ): Promise<TResponse> {
    isApplying.value = true
    try {
      const response =
        typeof producer === 'function' ? await producer() : await producer
      applyResponse(response)
      return response
    } finally {
      isApplying.value = false
    }
  }

  const context: AiFormFillContext = { hints, isApplying, clearHint }
  provide(AI_FORM_FILL_KEY, context)

  return {
    applyResponse,
    applyAsync,
    clearHint,
    hints,
    isApplying
  }
}

/**
 * Consumer-side composable. Returns a computed for the AI hint (if any)
 * of a single named field. Used internally by `FormTextField` and
 * `FormTextareaField` to render a provenance indicator without the form
 * primitives knowing anything about AI.
 */
export function useAiFieldHint(
  name: string
): ComputedRef<AiFieldHint | undefined> {
  const ctx = inject(AI_FORM_FILL_KEY, null)
  return computed(() => ctx?.hints[name])
}

/**
 * Returns the clear-hint function, or a no-op if no AI form fill context
 * is provided. Field components call this on user input so the sparkle
 * disappears once the user edits a field.
 */
export function useClearAiHint(): (name: string) => void {
  const ctx = inject(AI_FORM_FILL_KEY, null)
  return ctx?.clearHint ?? (() => {})
}

/**
 * Reactive "AI is currently filling" flag for the nearest AI context.
 * Returns a computed that's `false` when no context is provided, so
 * field components can call this unconditionally and ignore the result
 * outside of AI-enabled forms.
 */
export function useAiLoading(): ComputedRef<boolean> {
  const ctx = inject(AI_FORM_FILL_KEY, null)
  return computed(() => ctx?.isApplying.value ?? false)
}
